(function () {
  if (window.__jcSizeChartProxy) {
    return;
  }
  window.__jcSizeChartProxy = true;

  var TRIGGER_SELECTOR = '[data-size-chart-trigger]';
  var KIWI_LINK_SELECTOR = '.ks-chart-modal-link';
  var ACTIVE_CLASS = 'size-chart-proxy-active';

  // Prestige swaps the whole variant-picker block on every variant change, so the theme trigger is
  // missing for a moment mid-swap while Kiwi's link (pinned outside the block by
  // kiwi-size-chart-rerender-fix) stays put. Hiding the proxy on that gap would flash Kiwi's own
  // link back in on every size click, so a missing trigger only counts once it is still missing
  // after the swap has settled.
  var SETTLE_DELAY = 300;

  var settleTimer = null;
  var frame = 0;

  function kiwiLink() {
    return document.querySelector(KIWI_LINK_SELECTOR);
  }

  function proxyIsUsable() {
    return !!(document.querySelector(TRIGGER_SELECTOR) && kiwiLink());
  }

  // The state lives as a class on <html>, so triggers re-created by Prestige's variant re-render
  // inherit the right visibility with no re-binding. It has to fall as well as rise: the theme
  // trigger is server-rendered behind a schema setting, and Kiwi's link is injected a few hundred ms
  // after load and never appears at all for products with no size table. A class left standing after
  // either side disappears keeps Kiwi's own container hidden, leaving the page with no size chart
  // button at all.
  function sync() {
    var root = document.documentElement;

    if (proxyIsUsable()) {
      if (settleTimer) {
        clearTimeout(settleTimer);
        settleTimer = null;
      }

      root.classList.add(ACTIVE_CLASS);
      return;
    }

    if (settleTimer || !root.classList.contains(ACTIVE_CLASS)) {
      return;
    }

    settleTimer = setTimeout(function () {
      settleTimer = null;

      if (!proxyIsUsable()) {
        document.documentElement.classList.remove(ACTIVE_CLASS);
      }
    }, SETTLE_DELAY);
  }

  // Mutations arrive in bursts during a re-render; one recompute per frame is enough.
  function scheduleSync() {
    if (frame) {
      return;
    }

    frame = requestAnimationFrame(function () {
      frame = 0;
      sync();
    });
  }

  function start() {
    sync();

    new MutationObserver(scheduleSync).observe(document.body, { childList: true, subtree: true });

    // Theme editor: toggling the schema setting re-renders the section with or without our trigger.
    document.addEventListener('shopify:section:load', scheduleSync);
  }


  /* ---------------------------------------------------------------------------------------------------------------
   * Translating what Kiwi renders.
   *
   * The measurement names and the instructions are authored per chart inside Kiwi and come back identical for every
   * locale, so the modal is English on /nl/. These are the English strings as they are typed in Kiwi: they are match
   * patterns, which is why they sit next to the matching code instead of in the locale files. The translations come
   * from snippets/size-chart-dictionary.liquid, so the client can correct the wording in Translate & Adapt.
   *
   * Anything not listed here simply stays English rather than breaking the table.
   * ------------------------------------------------------------------------------------------------------------- */

  var TERM_SOURCES = {
    size: 'SIZE',
    front_length: 'FRONT LENGTH',
    chest: 'CHEST',
    shoulder: 'SHOULDER',
    sleeve: 'SLEEVE',
    waist: 'WAIST',
    length: 'LENGTH',
    inseam: 'INSEAM',
    front_rise: 'FRONT RISE',
    hip: 'HIP',
    // Kiwi's own spelling, kept verbatim so it still matches.
    shoulder_width: 'SCHOULDER WIDTH',
    sleeve_length: 'SLEEVE LENGTH',
    dress_length: 'DRESS LENGTH',
    centre_back_length: 'C/B LENGHT',
    half_shoulder_to_shoulder: '1/2 SHOULDER TO SHOULDER (BATWING SLEEVE) TOTAL'
  };

  /* One line per measurement, exactly as authored in Kiwi. Kiwi renders a block either as a single lump or as one
     paragraph per line depending on the chart, so both shapes are matched: the whole block first, then line by line. */
  var NOTE_SOURCES = {
    tolerance: ['Sizes can vary from 1-2 centimeters.'],
    tops: [
      'Front Length (A): Measure from the highest point of the shoulder down the front to the hem.',
      'Chest (B): Measure across the fullest part of the chest, from one side seam to the other.',
      'Shoulder (C): Measure straight across from the tip of one shoulder to the tip of the other.',
      'Sleeve (D): Measure from the shoulder seam down to the wrist.'
    ],
    tops_with_waist: [
      'Front Length (A): Measure from the highest point of the shoulder down the front to the hem.',
      'Chest (B): Measure across the fullest part of the chest, from one side seam to the other.',
      'Waist (C): Measure across the narrowest part of the waist, then double this measurement for the full circumference.',
      'Shoulder (D): Measure straight across from the tip of one shoulder to the tip of the other.',
      'Sleeve (E): Measure from the shoulder seam down to the wrist.'
    ],
    trousers: [
      'Length (A): Measured from the top of the waistband down to the bottom hem of the leg.',
      'Inseam (B): Measured along the inside leg from the crotch point (where the front and back seams meet) down to the hem.',
      'Waist (C): Measure straight across the waistband from left to right, then double this measurement to get the full waist circumference.',
      'Front rise (D): With the pants closed and laid flat and smooth, measure from the crotch seam up to the top of the waistband.',
      'Hip (E): With the pants closed and laid flat and smooth, measure across about 4 cm below the zipper, then double this measurement to get the full hip circumference.'
    ],
    skirts: [
      'Length (A): Measured from the top of the waistband down to the bottom hem.',
      'Waist (B): Measure straight across the waistband from left to right, then double this measurement to get the full waist circumference.',
      'Hip (C): Measured at the widest part of the skirt, then double this measurement to get the full hip circumference.'
    ],
    dresses: [
      'Chest (A): Measure across the fullest part of the chest from one side seam to the other, then double this measurement for the full circumference.',
      'Waist (B): Measure across the narrowest part of the waist, then double this measurement for the full circumference.',
      'Hip (C): Measure across the widest part of the hips, then double this measurement for the full circumference.',
      'Shoulder width (D): Measure straight across from the tip of one shoulder to the tip of the other.',
      'Sleeve length (E): Measure from the shoulder seam down to the wrist.',
      'Dress length (F): Measure from the highest point of the shoulder down to the hemline.'
    ]
  };

  var dictionary = null;

  function readDictionary() {
    if (dictionary !== null) {
      return dictionary;
    }

    var tag = document.querySelector('[data-size-chart-dictionary]');
    dictionary = false;

    if (tag) {
      try {
        dictionary = JSON.parse(tag.textContent);
      } catch (error) {
        /* a malformed payload leaves the modal in English, which is the state we started from */
      }
    }

    return dictionary;
  }

  function normalise(text) {
    return String(text).replace(/\s+/g, ' ').trim().toLowerCase();
  }

  /* "SHOULDER (C)" and "SHOULDER (D)" are the same measurement: the letter keys the diagram and changes with the
     table, so the suffix is put back untouched after the name itself is translated. */
  function translateTerm(raw, terms) {
    var match = String(raw).trim().match(/^(.*?)(\s*\([^)]*\))?$/);

    if (!match) {
      return null;
    }

    var name = normalise(match[1]);
    var suffix = match[2] || '';

    for (var id in TERM_SOURCES) {
      if (normalise(TERM_SOURCES[id]) === name && terms[id]) {
        return terms[id] + suffix;
      }
    }

    return null;
  }

  function translateTable(scope, terms) {
    var swapped = 0;

    scope.querySelectorAll('th, td').forEach(function (cell) {
      if (cell.children.length) {
        return;
      }

      var translated = translateTerm(cell.textContent, terms);

      if (translated && translated !== cell.textContent.trim()) {
        cell.textContent = translated;
        swapped++;
      }
    });

    return swapped;
  }

  /* Kiwi renders each instruction block as one rich-text lump, so it is matched whole and rebuilt whole. The
     translations are plain text — "Term (A): sentence" a line — and the markup is assembled here, which keeps HTML
     out of the locale files and out of anything the client edits in Translate & Adapt. */
  function escapeHtml(text) {
    return String(text).replace(/[&<>"]/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[character];
    });
  }

  function buildLine(line) {
    var trimmed = String(line).trim();

    if (!trimmed) {
      return '';
    }

    var split = trimmed.indexOf(': ');

    if (split === -1) {
      return '<p>' + escapeHtml(trimmed) + '</p>';
    }

    return '<p><strong>' + escapeHtml(trimmed.slice(0, split + 1)) + '</strong> ' + escapeHtml(trimmed.slice(split + 2)) + '</p>';
  }

  function replaceWith(element, html) {
    /* The deepest match is sometimes the paragraph itself, and dropping paragraphs inside it would nest them and
       print the sentence twice, so that one is replaced rather than filled. */
    if (element.tagName === 'P') {
      element.outerHTML = html;
    } else {
      element.innerHTML = html;
    }
  }

  /* Every occurrence, not just the first: 17 products in the catalogue have two charts attached in Kiwi, so the modal
     carries the whole block twice and translating one of them would leave the other in English. Elements that merely
     contain a smaller match are dropped, so only the innermost ones are rewritten. */
  function innermostMatches(scope, wanted) {
    var candidates = scope.querySelectorAll('div, p, span, td, li');
    var found = [];

    for (var i = 0; i < candidates.length; i++) {
      if (normalise(candidates[i].textContent) === wanted) {
        found.push(candidates[i]);
      }
    }

    return found.filter(function (element) {
      return !found.some(function (other) {
        return other !== element && element.contains(other);
      });
    });
  }

  function translateNotes(scope, notes) {
    var swapped = 0;

    for (var id in NOTE_SOURCES) {
      if (!notes[id]) {
        continue;
      }

      var sourceLines = NOTE_SOURCES[id];
      var targetLines = String(notes[id]).split('\n');

      // The whole block as one element, which is how most of the charts render it.
      var lumps = innermostMatches(scope, normalise(sourceLines.join('')));

      if (lumps.length) {
        lumps.forEach(function (lump) {
          replaceWith(lump, targetLines.map(buildLine).join(''));
          swapped++;
        });
        continue;
      }

      // Otherwise the block arrived as one paragraph per line, so each is matched on its own.
      for (var i = 0; i < sourceLines.length; i++) {
        if (!targetLines[i]) {
          continue;
        }

        innermostMatches(scope, normalise(sourceLines[i])).forEach(function (line) {
          replaceWith(line, buildLine(targetLines[i]));
          swapped++;
        });
      }
    }

    return swapped;
  }

  function translateModal() {
    var dict = readDictionary();

    if (!dict) {
      return;
    }

    var scope = document.querySelector('.ks-modal-content') || document.querySelector('[class*="ks-modal"]');

    if (!scope) {
      return false;
    }

    var swapped = translateTable(scope, dict.terms || {}) + translateNotes(scope, dict.notes || {});

    return swapped > 0;
  }

  /* Kiwi builds the modal body when the chart is first opened, so the swap waits for the click and then for the body
     to appear. The observer is scoped to the modal, gives up after a few seconds and never watches the page at rest. */
  function translateWhenOpened() {
    if (!readDictionary()) {
      return;
    }

    if (translateModal()) {
      return;
    }

    var observer = new MutationObserver(function () {
      if (translateModal()) {
        observer.disconnect();
        clearTimeout(timer);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    var timer = setTimeout(function () {
      observer.disconnect();
    }, 5000);
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    var trigger = target && target.closest ? target.closest(TRIGGER_SELECTOR) : null;

    if (!trigger) {
      return;
    }

    event.preventDefault();

    var link = kiwiLink();

    if (link) {
      link.click();
      translateWhenOpened();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
